import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { NAV_CONST } from '@/src/constants/nav';
import TextArea from '@/src/components/atom/TextArea';
import { usePrompt } from '@/src/hooks/usePrompt';
import DateRangePicker from '@/src/components/atom/DateRangePicker';
import dayjs from 'dayjs';
import ImageUploadInfo from '@/src/components/molecule/ImageUploadInfo';
import ImageUpload from '@/src/components/atom/ImageUpload';
import IconButton from '@/src/components/atom/Button/IconButton';
import { Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { APIError } from '@/src/models/common/apiError';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import {
  DEFAULT_VALUES,
  formFields,
  formSchema,
  FormValues,
} from '@/src/pages/Challenge/field';
import { UpsertChallengeRequestBody } from '@/src/apis/rest/challenge.dtos';
import { getCreditTemplateAPI } from '@/src/apis/rest/creditTemplate';
import {
  deleteChallengeAPI,
  getChallengeByIdAPI,
  putChallengeAPI,
} from '@/src/apis/rest/challenge';
import Select from '@/components/atom/Select';
import { UNIT_STRINGS } from '@/src/constants/unit';
import useModalState from '@/hooks/useModalState';
import AlertDialog from '@/components/atom/AlertDialog';
import { OPTIONS_CONST } from '@/src/constants/options';
import InfoFormSectionCard from '@/components/organism/InfoFormSectionCard/InfoFormSectionCard';
import SectionLabel from '@/components/molecule/SectionLabel';
import { formatter } from '@/src/utils/formatter';
import useDialog, { DialogType } from '@/src/hooks/useDialog';

const LIST_PATH = PATH.CHALLENGE_LIST;
const BACK_PATH = PATH.CHALLENGE_DETAIL;

type ModalState = 'NONE' | 'DELETE' | 'PRIORITY_ERROR' | 'BANNER_CONNECT_ERROR';

export default function ChallengeEditPage() {
  // util
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams();

  // form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUES,
    reValidateMode: 'onSubmit',
  });

  // state
  const [defaultData, setDefaultData] = useState('');
  const [isPorceReplace, setIsPorceReplace] = useState(false);
  const { modalState, handleOpenModal, handleToggleModal, handleCloseModal } =
    useModalState<ModalState>();
  const {
    Dialog: EditDialog,
    isOpen: isEditOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  const { data: detailData } = useQuery({
    queryKey: ['challenge-detail', id],
    queryFn: () => getChallengeByIdAPI(Number(id)),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH.replace(':id', String(id)), { replace: true });
      },
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const isProceeding =
    dayjs().isAfter(detailData?.startedAt) &&
    dayjs().isBefore(detailData?.endedAt);
  const isEnded = dayjs(detailData?.endedAt).isBefore(dayjs(), 'days');
  const isDeleteDisabled = () => {
    // 진행 중이거나 노출 중 인 경우 삭제 불가
    const isExposed = detailData?.isExposed;
    return isExposed || isProceeding || deleteMutation.isPending;
  };

  // query
  const { data: creditTemplateData, isFetched: isCreditTemplateFetched } =
    useQuery({
      queryKey: ['credit-template', 'challenge'],
      queryFn: () => {
        return getCreditTemplateAPI({
          page: 1,
          pageSize: 9999,
          category: '챌린지',
          isActive: true,
        });
      },
    });

  // mutation
  const putMutation = useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: number;
      params: UpsertChallengeRequestBody;
    }) => putChallengeAPI(id, params),
  });
  const deleteMutation = useMutation({
    mutationFn: deleteChallengeAPI,
  });

  // event
  const handleGoBack = () => {
    navigate(BACK_PATH.replace(':id', String(id)), { replace: true });
  };

  const isFormValid =
    form.formState.isValid &&
    !!form.watch('startedAt') &&
    !!form.watch('endedAt');

  // mutation
  const handleDelete = () => {
    deleteMutation.mutate(Number(id), {
      onSuccess: () => {
        setIsPorceReplace(true);
        toast({
          variant: 'success',
          title: TOAST_CONST.deleteSuccess.title,
          description: TOAST_CONST.deleteSuccess.description,
        });
        setTimeout(() => {
          navigate(LIST_PATH, { replace: true });
        }, 0);
      },
      onError: e => {
        const { error } = e as unknown as APIError;

        // TODO::
        if (error === '배너_에러_코드') {
          handleOpenModal('PRIORITY_ERROR');
        } else {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        }
      },
    });
  };

  const handleSubmit = (values: UpsertChallengeRequestBody) => {
    putMutation.mutate(
      {
        id: Number(id),
        params: {
          ...values,
          creditTemplateId: Number(values.creditTemplateId),
        },
      },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          navigate(BACK_PATH.replace(':id', String(id)));
        },
        onError: e => {
          const { error } = e as unknown as APIError;

          if (error === 'CANNOT_PRIORITY_EXPOSE_OVER_THAN_FIVE') {
            handleOpenModal('PRIORITY_ERROR');
          } else {
            toast({
              variant: 'error',
              title: TOAST_CONST.error.title,
              description: e.message,
            });
          }
        },
      },
    );
  };

  // render
  const startedDateRangeDefaultMessage =
    isProceeding || isEnded ? (
      <p className="flex gap-1">
        <Info size={20} />
        {isEnded
          ? '종료된 컨텐츠는 수정이 불가능합니다.'
          : '진행중인 컨텐츠는 종료일만 수정 가능합니다.'}
      </p>
    ) : undefined;
  const getDateDisabledMatcher = (date: Date) => {
    if (dayjs().isAfter(date, 'D')) return true;

    return false;
  };
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <div className="flex gap-3">
            <Button
              disabled={!isFormValid}
              type="button"
              onClick={openEditDialog}
              className="w-40"
              buttonSize="medium"
              isLoading={putMutation.isPending}
            >
              <div className="flex items-center gap-x-2">
                {BUTTON_CONST.save}
              </div>
            </Button>
          </div>
        </div>
      </BottomFixedContainer>
    );
  };

  // effect
  useEffect(() => {
    if (detailData) {
      const resetFormData = {
        ...detailData,
        startedDateRange: {
          from: new Date(detailData?.startedAt),
          to: new Date(detailData?.endedAt),
        },
        creditTemplateId: detailData?.creditTemplate?.id,
        thumbnailImageId: detailData?.thumbnailImage?.id,
        approveExampleImageId: detailData?.approveExampleImage?.id,
        rejectExampleImageId: detailData?.rejectExampleImage?.id,
        generateChallengeInfos: detailData?.challengeInfos || [],
        generateChallengeCautions: detailData?.challengeCautions || [],
        removeInfoIds: [],
        removeCautionIds: [],
      };

      form.reset(resetFormData);
      setDefaultData(JSON.stringify(resetFormData));
    }
  }, [form, detailData]);

  useEffect(() => {
    if (creditTemplateData && isCreditTemplateFetched && detailData) {
      form.setValue('creditTemplateId', detailData.creditTemplate.id);
      form.trigger('creditTemplateId');
    }
  }, [form, creditTemplateData, detailData, isCreditTemplateFetched]);

  // usePrompt
  usePrompt(
    defaultData !== JSON.stringify(form.watch()) &&
      !form.formState.isSubmitted &&
      !isPorceReplace,
  );

  return (
    <>
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.challengeManagement,
          NAV_CONST.challengeEdit,
        ]}
        pageTitle={NAV_CONST.challengeEdit}
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative mb-[100px] grid grid-cols-[1fr_1fr] gap-4"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="챌린지 기본 정보" className="col-span-2">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-6">
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name={formFields.startedDateRange.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        isRequired
                        label="참여 기간"
                        error={error}
                        defaultMessage={startedDateRangeDefaultMessage}
                      >
                        <DateRangePicker
                          {...field}
                          onChange={e => {
                            field.onChange(
                              isProceeding
                                ? {
                                    ...e,
                                    from: new Date(
                                      dayjs(detailData?.startedAt).format(
                                        'YYYY-MM-DD',
                                      ),
                                    ),
                                  }
                                : e,
                            );

                            form.setValue(
                              'startedAt',
                              isProceeding
                                ? dayjs(detailData?.startedAt).format(
                                    'YYYY-MM-DD',
                                  )
                                : e?.from
                                  ? dayjs(e?.from).format('YYYY-MM-DD')
                                  : '',
                            );
                            form.setValue(
                              'endedAt',
                              e?.to ? dayjs(e?.to).format('YYYY-MM-DD') : '',
                            );
                          }}
                          allowClear={!(isEnded || isProceeding)}
                          dateDisabledMatcher={getDateDisabledMatcher}
                          triggerProps={{
                            disabled: isEnded,
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />

                <FormField
                  control={form.control}
                  name={formFields.isExposed.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="노출 여부"
                        isRequired={true}
                        error={error}
                      >
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.exposed, value: 'true' },
                            { label: OPTIONS_CONST.notExposed, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.priorityDisplayYn.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="우선 노출 여부"
                        isRequired={true}
                        error={error}
                      >
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.applied, value: 'true' },
                            { label: OPTIONS_CONST.notApplied, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.thumbnailImageId.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        isRequired
                        label="썸네일 이미지"
                        defaultMessage={
                          <ImageUploadInfo widthSize={335} heightSize={335} />
                        }
                        error={error}
                      >
                        <ImageUpload
                          width={128}
                          height={128}
                          widthText={335}
                          heightText={335}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          value={form.watch('thumbnailImage')?.bufferFullUrl}
                          onChange={e => {
                            field.onChange(e.id);
                            form.setValue('thumbnailImage', e);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                      form.setValue(
                                        'thumbnailImage',
                                        undefined,
                                      );
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name={formFields.creditTemplateId.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        className="w-full gap-2 [&>div:last-child]:w-full"
                        label="지급 크레딧"
                        isRequired
                        error={error}
                      >
                        <Select
                          option={[
                            {
                              optionList: creditTemplateData?.rows?.map(
                                template => ({
                                  label: `[챌린지] ${template.title} (${formatter.NUM_COMMA(template.credit)}${UNIT_STRINGS.CREDIT})`,
                                  value: template.id.toString(),
                                }),
                              ) || [
                                {
                                  label: '등록된 크레딧 카테고리가 없습니다.',
                                  value: '',
                                },
                              ],
                            },
                          ]}
                          rootProps={{
                            value: field.value?.toString(),
                            onValueChange: value => {
                              form.setValue('creditTemplateId', Number(value));
                              field.onChange(Number(value));
                            },
                          }}
                          valueProps={{
                            placeholder: '크레딧 카테고리 선택',
                          }}
                          {...field}
                        />
                      </FormContent>
                    );
                  }}
                />
                <SectionLabel
                  labelProps={{
                    className: 'translate-y-2',
                  }}
                  label="참여자 수"
                  value={
                    <Input
                      containerClassName="flex-1"
                      value={`${detailData?.participantsCnt.toLocaleString() || 0}명`}
                      disabled
                    />
                  }
                />
                <FormField
                  control={form.control}
                  name={formFields.approveExampleImageId.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        isRequired={true}
                        label="승인 예시 이미지"
                        defaultMessage={
                          <ImageUploadInfo widthSize={335} heightSize={335} />
                        }
                        error={error}
                      >
                        <ImageUpload
                          width={128}
                          height={128}
                          widthText={335}
                          heightText={335}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          value={
                            form.watch('approveExampleImage')?.bufferFullUrl
                          }
                          onChange={e => {
                            field.onChange(e.id);
                            form.setValue('approveExampleImage', e);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                      form.setValue(
                                        'approveExampleImage',
                                        undefined,
                                      );
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.rejectExampleImageId.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        isRequired={true}
                        label="거절 예시 이미지"
                        defaultMessage={
                          <ImageUploadInfo widthSize={335} heightSize={335} />
                        }
                        error={error}
                      >
                        <ImageUpload
                          width={128}
                          height={128}
                          widthText={335}
                          heightText={335}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          value={
                            form.watch('rejectExampleImage')?.bufferFullUrl
                          }
                          onChange={e => {
                            field.onChange(e.id);
                            form.setValue('rejectExampleImage', e);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                      form.setValue(
                                        'rejectExampleImage',
                                        undefined,
                                      );
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <SectionLabel
                  labelProps={{
                    className: 'translate-y-2',
                  }}
                  label="등록 일시"
                  value={
                    <Input
                      containerClassName="flex-1"
                      value={dayjs(detailData?.createdAt).format(
                        'YYYY-MM-DD HH:mm:ss',
                      )}
                      disabled
                    />
                  }
                />
                <SectionLabel
                  labelProps={{
                    className: 'translate-y-2',
                  }}
                  label="등록 관리자"
                  value={
                    <Input
                      containerClassName="flex-1"
                      value={detailData?.administrator?.name || '-'}
                      disabled
                    />
                  }
                />
              </div>
            </div>
          </SectionCard>
          <SectionCard cardTitle="챌린지 내용" className="col-span-2">
            <div className="flex flex-1 flex-col gap-6">
              <FormField
                control={form.control}
                name={formFields.title.name}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent isRequired label="제목" error={error}>
                      <Input
                        onClear={() => field.onChange(undefined)}
                        placeholder="제목을 입력하세요"
                        {...field}
                      />
                    </FormContent>
                  );
                }}
              />
              <FormField
                control={form.control}
                name={formFields.content.name}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent
                      className="col-span-3"
                      label="내용"
                      isRequired
                      error={error}
                    >
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <TextArea
                            className="h-[500px]"
                            placeholder="챌린지 내용을 입력하세요"
                            {...field}
                          />
                        </div>
                      </div>
                    </FormContent>
                  );
                }}
              />
            </div>
          </SectionCard>
          <InfoFormSectionCard
            cardTitle="안내사항 영역"
            form={form}
            field={formFields.generateChallengeInfos.name}
            removeField="removeInfoIds"
          />
          <InfoFormSectionCard
            cardTitle="유의사항 영역"
            form={form}
            field={formFields.generateChallengeCautions.name}
            removeField="removeCautionIds"
            buttonText="유의사항 추가하기"
          />
          {renderBottomFixedContent()}
        </form>
      </Form>

      {/* Modal */}
      {modalState === 'DELETE' && (
        <AlertDialog
          open={modalState === 'DELETE'}
          onOpenChange={() => handleToggleModal('DELETE')}
          okText={BUTTON_CONST.ok}
          onOk={handleDelete}
          title="챌린지 삭제"
          okButtonProps={{
            isLoading: deleteMutation.isPending,
          }}
        >
          <p className="whitespace-pre-wrap">
            챌린지를 삭제하시겠습니까?
            <br />
            삭제된 챌린지는 사용자에게 노출되지 않습니다.
          </p>
        </AlertDialog>
      )}
      {modalState === 'PRIORITY_ERROR' && (
        <AlertDialog
          open={modalState === 'PRIORITY_ERROR'}
          onOpenChange={() => handleToggleModal('PRIORITY_ERROR')}
          okText={BUTTON_CONST.ok}
          onOk={handleCloseModal}
          title="우선 노출 여부 지정 불가"
          cancelButtonProps={{
            className: 'hidden',
          }}
        >
          <p className="whitespace-pre-wrap">
            이미 해당 챌린지 기간 내 우선노출 지정 개수가 5개 있습니다.
            <br />
            다른 챌린지를 확인 후 재시도 해주세요.
          </p>
        </AlertDialog>
      )}
      {modalState === 'BANNER_CONNECT_ERROR' && (
        <AlertDialog
          open={modalState === 'BANNER_CONNECT_ERROR'}
          onOpenChange={() => handleToggleModal('BANNER_CONNECT_ERROR')}
          okText={BUTTON_CONST.ok}
          onOk={handleCloseModal}
          title="삭제 불가"
          cancelButtonProps={{
            className: 'hidden',
          }}
        >
          <p className="whitespace-pre-wrap">
            연결된 배너가 있어서 삭제가 불가능합니다.
            <br />
            배너를 확인해주세요.
          </p>
        </AlertDialog>
      )}
      {isEditOpen && (
        <EditDialog
          open={isEditOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openEditDialog();
            } else {
              closeEditDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={putMutation.isPending}
        />
      )}
    </>
  );
}
